import { XHRRequestCanceler } from '../../../../Libs/xhr/XHRRequestCanceler';
import {
    CompagnonDataServiceName,
    CompagnonEndpoints,
    ICompagnonDataService,
} from '../../../DataServices/CompagnonDataService';
import { Lazy } from '../../../Helpers/Lazy';
import { BaseApiDataResponse } from '../../../Models/ApiData';
import {
    CompagnonDetails,
    CompagnonDetailsApiDataResponse,
    CompagnonListOrder,
    CompagnonListResponse,
    CompagnonRole,
    CompagnonSummaryListResponse,
} from '../../../Models/Compagnon/Compagnon';
import { ExportFileType, ExportUtils } from '../../../Utils/ExportUtils';
import { IoC } from '../../ServicesContainer';
import { BaseReduxService } from '../Api/BaseReduxService';
import { AuthServiceName, IAuthService } from '../Authentication/AuthenticationService';
import { DownloadServiceName, IDownloadService } from '../Download/DownloadService';
import { I18nServiceName, II18nService } from '../I18n/I18nService';

export interface ICompagnonService {
    getCompagnons: (
        cts: XHRRequestCanceler,
        params: {
            count?: number;
            role?: CompagnonRole;
            state?: boolean;
            search?: string;
            have_front_access?: boolean;
            order?: CompagnonListOrder;
        }
    ) => Promise<CompagnonListResponse>;

    listCompagnonSummary: (
        cts: XHRRequestCanceler,
        params: {
            search?: string;
            role?: CompagnonRole;
        }
    ) => Promise<CompagnonSummaryListResponse>;

    getCompagnon: (
        cts: XHRRequestCanceler,
        compagnonId: string
    ) => Promise<CompagnonDetailsApiDataResponse>;

    updateCompagnon: (
        compagnonId: string,
        compagnon: CompagnonDetails,
        cts: XHRRequestCanceler
    ) => Promise<BaseApiDataResponse>;

    createCompagnon: (
        compagnon: CompagnonDetails,
        cts: XHRRequestCanceler
    ) => Promise<CompagnonDetailsApiDataResponse>;

    exportCompagnonList: (
        cts: XHRRequestCanceler,
        search: string,
        isActive?: boolean,
        order?: CompagnonListOrder
    ) => void;
}

class CompagnonService extends BaseReduxService implements ICompagnonService {
    private readonly compagnonDataService: Lazy<ICompagnonDataService>;
    private readonly i18nService: Lazy<II18nService>;
    private readonly downloadService: Lazy<IDownloadService>;
    private readonly authService: Lazy<IAuthService>;

    constructor() {
        super();
        this.compagnonDataService = IoC.getLazy<ICompagnonDataService>(CompagnonDataServiceName);
        this.i18nService = IoC.getLazy<II18nService>(I18nServiceName);
        this.downloadService = IoC.getLazy<IDownloadService>(DownloadServiceName);
        this.authService = IoC.getLazy<IAuthService>(AuthServiceName);
    }

    public listCompagnonSummary = async (
        cts: XHRRequestCanceler,
        params: { search?: string | undefined; role?: CompagnonRole }
    ): Promise<CompagnonSummaryListResponse> => {
        try {
            return this.compagnonDataService.value().listCompagnonSummary(
                {
                    search: params.search,
                    role: params.role,
                },
                cts
            );
        } catch (err) {
            return Promise.reject(err);
        }
    };

    public getCompagnons = async (
        cts: XHRRequestCanceler,
        params: {
            count?: number;
            role?: CompagnonRole;
            state?: boolean;
            search?: string;
            have_front_access?: boolean;
            order?: CompagnonListOrder;
        }
    ) => {
        try {
            return this.compagnonDataService.value().getCompagnons(
                {
                    compagnon_role: params.role,
                    is_active: params.state,
                    search: params.search,
                    Count: params.count,
                    have_front_access: params.have_front_access,
                    order: params.order,
                },
                cts
            );
        } catch (err) {
            return Promise.reject(err);
        }
    };

    public getCompagnon = (cts: XHRRequestCanceler, compagnonId: string) => {
        try {
            return this.compagnonDataService.value().getCompagnon(cts, compagnonId);
        } catch (err) {
            return Promise.reject(err);
        }
    };

    public updateCompagnon = async (
        compagnonId: string,
        compagnon: CompagnonDetails,
        cts: XHRRequestCanceler
    ) => {
        try {
            const response = await this.compagnonDataService
                .value()
                .updateCompagnon(compagnonId, compagnon, cts);
            if (!response.is_success) {
                this.toastApiError(
                    this.i18nService.value().getString(`Error_${response.error_code}` || '')
                );
            }
            return Promise.resolve(response);
        } catch (err) {
            return Promise.reject(err);
        }
    };

    public createCompagnon = async (
        compagnon: CompagnonDetails,
        cts: XHRRequestCanceler
    ): Promise<CompagnonDetailsApiDataResponse> => {
        try {
            const response = await this.compagnonDataService
                .value()
                .createCompagnon(compagnon, cts);
            if (!response.is_success) {
                this.toastApiError(
                    this.i18nService.value().getString(`Error_${response.error_code}` || '')
                );
            }
            return Promise.resolve(response);
        } catch (err) {
            this.toastApiError(this.i18nService.value().getString(`Error_${(err as any).error_code}` || ''));
            return Promise.reject(err);
        }
    };

    public exportCompagnonList = async (
        cts: XHRRequestCanceler,
        search: string,
        isActive?: boolean,
        order?: CompagnonListOrder | undefined
    ): Promise<void> => {
        try {
            const params: { [key: string]: string } = {};
            if (isActive === true) {
                params['is_active'] = isActive.valueOf().toString();
            }
            params['search'] = search;
            if (order) {
                params['order'] = order.valueOf();
            }
            const url: string = ExportUtils.getExportUrlWithParams(
                {
                    url: CompagnonEndpoints.EXPORT_COMPAGNON_LIST,
                    auth: await this.authService.value().getAuthorization(),
                },
                params
            );
            this.downloadService.value().downloadFile(url, 'Personnel', ExportFileType.EXCEL, cts);
        } catch (err) {
            Promise.reject(err);
        }
    };
}

const CompagnonServiceName = 'ControlService';
export { CompagnonService, CompagnonServiceName };
